import { useEffect, useRef, useState, useCallback } from "react";
import soundtrack from "./assets/soundtrack.mp3";

const GRID = 20;
const TICK = 130;

function randomFood(snake) {
  const taken = new Set(snake.map(s => `${s.x},${s.y}`));
  let x, y;
  do {
    x = Math.floor(Math.random() * GRID);
    y = Math.floor(Math.random() * GRID);
  } while (taken.has(`${x},${y}`));
  return { x, y };
}

function drawScene(ctx, W, H, game, tick) {
  const CELL = Math.floor(Math.min(W, H) / GRID);
  const OX = Math.floor((W - CELL * GRID) / 2);
  const OY = Math.floor((H - CELL * GRID) / 2);

  // ── Background
  ctx.fillStyle = "#0a0015";
  ctx.fillRect(0, 0, W, H);

  // ── Retro sun (behind grid)
  const sunX = W / 2;
  const sunY = OY - 10;
  const sunR = Math.min(W, H) * 0.22;
  const grad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR);
  grad.addColorStop(0,   "#ff8800");
  grad.addColorStop(0.5, "#ff4400");
  grad.addColorStop(1,   "rgba(255,0,100,0)");
  ctx.save();
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  // Horizontal stripes cutout
  ctx.fillStyle = "#0a0015";
  for (let i = 0; i < 8; i++) {
    const ly = sunY - sunR * 0.6 + i * (sunR * 0.14);
    const lh = sunR * 0.04 + i * 0.5;
    ctx.fillRect(sunX - sunR, ly, sunR * 2, lh);
  }
  // Glow ring
  ctx.strokeStyle = "#ff00aa";
  ctx.lineWidth = 3;
  ctx.shadowColor = "#ff00aa";
  ctx.shadowBlur = 18;
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR * 0.98, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // ── Grid background
  ctx.save();
  ctx.fillStyle = "#0c0020";
  ctx.fillRect(OX, OY, CELL * GRID, CELL * GRID);

  // Grid lines
  ctx.strokeStyle = "rgba(80,0,180,0.35)";
  ctx.lineWidth = 0.5;
  ctx.shadowBlur = 0;
  for (let i = 0; i <= GRID; i++) {
    ctx.beginPath();
    ctx.moveTo(OX + i * CELL, OY);
    ctx.lineTo(OX + i * CELL, OY + CELL * GRID);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(OX, OY + i * CELL);
    ctx.lineTo(OX + CELL * GRID, OY + i * CELL);
    ctx.stroke();
  }

  // Perspective horizon lines below grid
  ctx.strokeStyle = "#ff00aa";
  for (let i = 0; i < 10; i++) {
    const alpha = (1 - i / 10) * 0.5;
    ctx.strokeStyle = `rgba(255,0,170,${alpha})`;
    ctx.lineWidth = 0.8;
    const y = OY + CELL * GRID + i * 8;
    const spread = 1 + i * 0.25;
    ctx.beginPath();
    ctx.moveTo(W / 2 - (CELL * GRID / 2) * spread, y);
    ctx.lineTo(W / 2 + (CELL * GRID / 2) * spread, y);
    ctx.stroke();
  }
  ctx.restore();

  const { snake, food, alive } = game;
  if (!snake.length) return;

  const t = Date.now();

  // ── Food
  const fx = OX + food.x * CELL + CELL / 2;
  const fy = OY + food.y * CELL + CELL / 2;
  const pulse = 0.75 + 0.25 * Math.sin(t * 0.006);
  const fr = (CELL * 0.38) * pulse;
  ctx.save();
  ctx.shadowColor = "#ffee00";
  ctx.shadowBlur = 18;
  // Rotating diamond
  ctx.translate(fx, fy);
  ctx.rotate(t * 0.003);
  ctx.fillStyle = "#ffee00";
  ctx.beginPath();
  ctx.moveTo(0, -fr);
  ctx.lineTo(fr * 0.6, 0);
  ctx.lineTo(0, fr);
  ctx.lineTo(-fr * 0.6, 0);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // ── Snake
  snake.forEach((seg, i) => {
    const sx = OX + seg.x * CELL;
    const sy = OY + seg.y * CELL;
    const pad = CELL * 0.1;
    const r = CELL * 0.22;
    const w = CELL - pad * 2;
    const h = CELL - pad * 2;
    const x = sx + pad;
    const y = sy + pad;

    if (i === 0) {
      // Head — cyan with strong glow
      const headPulse = 1 + 0.06 * Math.sin(t * 0.01);
      const hp = pad * headPulse;
      const hw = CELL - hp * 2;
      ctx.save();
      ctx.shadowColor = "#00ffcc";
      ctx.shadowBlur = 22;
      ctx.fillStyle = "#00ffcc";
      roundRect(ctx, sx + hp, sy + hp, hw, hw, r);
      ctx.fill();
      // Inner highlight
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      roundRect(ctx, sx + hp + 3, sy + hp + 3, hw * 0.45, hw * 0.3, 3);
      ctx.fill();
      ctx.restore();

      // Eyes
      if (alive) {
        ctx.save();
        ctx.fillStyle = "#0a0015";
        ctx.shadowBlur = 0;
        const ex = sx + CELL / 2;
        const ey = sy + CELL / 2;
        const eyeR = CELL * 0.1;
        const eyeOff = CELL * 0.2;
        ctx.beginPath();
        ctx.arc(ex - eyeOff, ey - eyeOff, eyeR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(ex + eyeOff, ey - eyeOff, eyeR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    } else {
      // Body segment — gradient from magenta to purple
      const ratio = i / snake.length;
      const r2 = Math.round(255 * (1 - ratio * 0.5));
      const g2 = 0;
      const b2 = Math.round(170 + ratio * 85);
      const col = `rgb(${r2},${g2},${b2})`;
      ctx.save();
      ctx.shadowColor = col;
      ctx.shadowBlur = 10;
      ctx.fillStyle = col;
      roundRect(ctx, x, y, w, h, r);
      ctx.fill();
      ctx.restore();
    }
  });

  // ── Particles
  if (game.particles) {
    game.particles.forEach(p => {
      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }

  // ── Scanlines overlay
  ctx.save();
  ctx.globalAlpha = 0.07;
  ctx.fillStyle = "#000";
  for (let y = 0; y < H; y += 4) {
    ctx.fillRect(0, y, W, 2);
  }
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ViperGame() {
  const canvasRef  = useRef(null);
  const gameRef    = useRef({
    snake: [], dir: { x: 1, y: 0 }, nextDir: { x: 1, y: 0 },
    food: { x: 15, y: 10 }, particles: [],
    alive: false, score: 0, turnLocked: false,
  });
  const rafRef     = useRef(null);
  const lastTick   = useRef(0);
  const touchStart = useRef(null);
  const soundtrackRef = useRef(null);

  const [phase,   setPhase]   = useState("start");
  const [score,   setScore]   = useState(0);
  const [hiScore, setHiScore] = useState(0);

  // Creates the looping soundtrack element once for the session.
  useEffect(() => {
    if (soundtrackRef.current) return;
    const audio = new Audio(soundtrack);
    audio.loop = true;
    audio.volume = 0.6;
    soundtrackRef.current = audio;
    return () => {
      audio.pause();
    };
  }, []);

  // ── Main loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let tickN = 0;

    const loop = (ts) => {
      rafRef.current = requestAnimationFrame(loop);
      const g = gameRef.current;

      // Tick
      if (g.alive && ts - lastTick.current >= TICK) {
        lastTick.current = ts;
        tickN++;
        step(g, canvas.width, canvas.height);
      }

      // Update particles
      if (g.particles) {
        g.particles = g.particles.filter(p => {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.12;
          p.life -= 0.03;
          return p.life > 0;
        });
      }

      drawScene(ctx, canvas.width, canvas.height, g, tickN);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Steps the game state forward by one tick.
  const step = (g, W, H) => {
    g.dir = { ...g.nextDir };
    // Unlock turning after applying the queued direction.
    g.turnLocked = false;
    const head = g.snake[0];
    const nh = {
      x: (head.x + g.dir.x + GRID) % GRID,
      y: (head.y + g.dir.y + GRID) % GRID,
    };

    // Self collision
    if (g.snake.some(s => s.x === nh.x && s.y === nh.y)) {
      g.alive = false;
      // Explosion particles
      const CELL = Math.floor(Math.min(W, H) / GRID);
      const OX = Math.floor((W - CELL * GRID) / 2);
      const OY = Math.floor((H - CELL * GRID) / 2);
      g.snake.forEach(seg => {
        for (let i = 0; i < 5; i++) {
          const angle = Math.random() * Math.PI * 2;
          const spd = 1.5 + Math.random() * 3;
          g.particles.push({
            x: OX + seg.x * CELL + CELL / 2,
            y: OY + seg.y * CELL + CELL / 2,
            vx: Math.cos(angle) * spd,
            vy: Math.sin(angle) * spd - 1,
            life: 1,
            r: 4 + Math.random() * 4,
            color: Math.random() > 0.5 ? "#ff00aa" : "#00ffcc",
          });
        }
      });
      setPhase("dead");
      setHiScore(prev => Math.max(prev, g.score));
      return;
    }

    const ate = nh.x === g.food.x && nh.y === g.food.y;
    g.snake.unshift(nh);
    if (!ate) {
      g.snake.pop();
    } else {
      // Eat particles
      const CELL = Math.floor(Math.min(W, H) / GRID);
      const OX = Math.floor((W - CELL * GRID) / 2);
      const OY = Math.floor((H - CELL * GRID) / 2);
      for (let i = 0; i < 12; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 2 + Math.random() * 4;
        g.particles.push({
          x: OX + g.food.x * CELL + CELL / 2,
          y: OY + g.food.y * CELL + CELL / 2,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd - 2,
          life: 1,
          r: 3 + Math.random() * 5,
          color: "#ffee00",
        });
      }
      g.food = randomFood(g.snake);
      g.score += 10;
      setScore(g.score);
    }
  };

  // Starts or resumes the soundtrack without resetting playback.
  const playSoundtrack = useCallback(() => {
    const audio = soundtrackRef.current;
    if (!audio || !audio.paused) return;
    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }, []);

  // Starts a new run by reinitializing the gameplay state.
  const startGame = useCallback(() => {
    const g = gameRef.current;
    const snake = [{ x: 10, y: 10 }, { x: 9, y: 10 }, { x: 8, y: 10 }];
    g.snake      = snake;
    g.dir        = { x: 1, y: 0 };
    g.nextDir    = { x: 1, y: 0 };
    g.food       = randomFood(snake);
    g.particles  = [];
    g.alive      = true;
    g.score      = 0;
    lastTick.current = performance.now();
    setScore(0);
    setPhase("playing");
    playSoundtrack();
  }, [playSoundtrack]);

  // ── Keyboard
  useEffect(() => {
    // Handles keyboard input for movement and starting a new run.
    const onKey = (e) => {
      const g = gameRef.current;
      const isMoveKey = [
        "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
        "w", "W", "a", "A", "s", "S", "d", "D",
      ].includes(e.key);
      if (isMoveKey && g.turnLocked) {
        if (e.key.startsWith("Arrow")) e.preventDefault();
        return;
      }
      const d = g.dir;
      switch (e.key) {
        case "ArrowUp":    case "w": case "W": if (d.y !==  1) { g.nextDir = { x:  0, y: -1 }; g.turnLocked = true; } break;
        case "ArrowDown":  case "s": case "S": if (d.y !== -1) { g.nextDir = { x:  0, y:  1 }; g.turnLocked = true; } break;
        case "ArrowLeft":  case "a": case "A": if (d.x !==  1) { g.nextDir = { x: -1, y:  0 }; g.turnLocked = true; } break;
        case "ArrowRight": case "d": case "D": if (d.x !== -1) { g.nextDir = { x:  1, y:  0 }; g.turnLocked = true; } break;
        case " ": case "Enter":
          if (phase === "start" || phase === "dead") startGame();
          break;
        default: return;
      }
      if (e.key.startsWith("Arrow")) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, startGame]);

  // ── Touch
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }, []);

  // Handles swipe input to update direction once per tick.
  const onTouchEnd = useCallback((e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;

    if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      if (phase === "start" || phase === "dead") startGame();
      return;
    }
    const g = gameRef.current;
    if (g.turnLocked) return;
    const d = g.dir;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0 && d.x !== -1) { g.nextDir = { x:  1, y: 0 }; g.turnLocked = true; }
      else if (dx < 0 && d.x !==  1) { g.nextDir = { x: -1, y: 0 }; g.turnLocked = true; }
    } else {
      if (dy > 0 && d.y !== -1) { g.nextDir = { x: 0, y:  1 }; g.turnLocked = true; }
      else if (dy < 0 && d.y !==  1) { g.nextDir = { x: 0, y: -1 }; g.turnLocked = true; }
    }
  }, [phase, startGame]);

  // ── D-pad
  // Renders a d-pad button that updates direction once per tick.
  const dpad = (dx, dy, label) => {
    // Handles pointer presses for d-pad movement.
    const press = (e) => {
      e.preventDefault();
      const g = gameRef.current;
      if (g.turnLocked) return;
      const d = g.dir;
      if (dx !== 0 && d.x !== -dx) { g.nextDir = { x: dx, y:  0 }; g.turnLocked = true; }
      if (dy !== 0 && d.y !== -dy) { g.nextDir = { x:  0, y: dy }; g.turnLocked = true; }
    };
    return (
      <button onPointerDown={press} style={{
        width: 56, height: 56, borderRadius: 10,
        background: "rgba(255,0,170,0.13)",
        border: "1.5px solid rgba(255,0,170,0.5)",
        color: "#ff00aa", fontSize: 22,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", touchAction: "none", userSelect: "none",
        WebkitUserSelect: "none",
        boxShadow: "0 0 10px rgba(255,0,170,0.3)",
        transition: "background 0.1s, box-shadow 0.1s",
      }}
        onPointerEnter={e => { e.currentTarget.style.background = "rgba(255,0,170,0.32)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(255,0,170,0.6)"; }}
        onPointerLeave={e => { e.currentTarget.style.background = "rgba(255,0,170,0.13)"; e.currentTarget.style.boxShadow = "0 0 10px rgba(255,0,170,0.3)"; }}
      >{label}</button>
    );
  };

  const fmt = n => String(n).padStart(5, "0");

  return (
    <div
      style={{ width: "100vw", height: "100svh", background: "#0a0015", overflow: "hidden",
        position: "relative", fontFamily: "'Courier New', monospace" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Canvas fills everything */}
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} />

      {/* Vignette */}
      <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none",
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.72) 100%)" }} />

      {/* HUD */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 24px",
        background: "linear-gradient(180deg, rgba(10,0,21,0.95) 0%, transparent 100%)",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "#ff00aa", letterSpacing: 4, textShadow: "0 0 10px #ff00aa" }}>HI-SCORE</div>
          <div style={{ fontSize: 26, color: "#ff00aa", fontWeight: 900, textShadow: "0 0 20px #ff00aa, 0 0 40px #ff00aa" }}>{fmt(hiScore)}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontSize: "clamp(20px, 5vw, 38px)", fontWeight: 900, letterSpacing: 8,
            background: "linear-gradient(180deg, #00ffcc 0%, #ff00aa 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 10px #00ffcc) drop-shadow(0 0 20px #ff00aa)",
          }}>V I P E R</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: "#00ffcc", letterSpacing: 4, textShadow: "0 0 10px #00ffcc" }}>SCORE</div>
          <div style={{ fontSize: 26, color: "#00ffcc", fontWeight: 900, textShadow: "0 0 20px #00ffcc, 0 0 40px #00ffcc" }}>{fmt(score)}</div>
        </div>
      </div>

      {/* START */}
      {phase === "start" && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 20,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(10,0,21,0.75)",
        }}>
          <div style={{
            fontSize: "clamp(56px, 15vw, 110px)", fontWeight: 900, letterSpacing: 10,
            background: "linear-gradient(180deg, #00ffcc 0%, #cc00ff 50%, #ff00aa 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 30px #00ffcc) drop-shadow(0 0 60px #ff00aa)",
            animation: "vp 2s ease-in-out infinite", marginBottom: 6,
          }}>VIPER</div>
          <div style={{ color: "#cc00ff", fontSize: 12, letterSpacing: 7, marginBottom: 48,
            textShadow: "0 0 14px #cc00ff" }}>◆ SYNTHWAVE EDITION ◆</div>
          <div style={{
            border: "1px solid rgba(0,255,204,0.3)", padding: "18px 40px",
            borderRadius: 4, marginBottom: 32, textAlign: "center",
            background: "rgba(0,255,204,0.03)",
          }}>
            <div style={{ color: "#00ffcc", fontSize: 11, letterSpacing: 4, marginBottom: 12,
              textShadow: "0 0 8px #00ffcc" }}>CONTROLS</div>
            <div style={{ color: "#887799", fontSize: 12, lineHeight: 2.3, letterSpacing: 1 }}>
              🎮 ARROW KEYS · WASD<br/>📱 SWIPE · D-PAD ON SCREEN<br/>🐍 EAT · GROW · SURVIVE
            </div>
          </div>
          <button onClick={startGame} style={{
            background: "linear-gradient(135deg, #ff00aa, #cc00ff)", border: "none",
            color: "#fff", fontSize: 15, letterSpacing: 6, padding: "15px 56px",
            borderRadius: 4, cursor: "pointer", fontFamily: "'Courier New', monospace",
            fontWeight: 900, textTransform: "uppercase",
            boxShadow: "0 0 40px rgba(255,0,170,0.8), 0 0 80px rgba(204,0,255,0.4)",
            animation: "bg 1.4s ease-in-out infinite alternate",
          }}>▶ START GAME</button>
        </div>
      )}

      {/* GAME OVER */}
      {phase === "dead" && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 20,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(10,0,21,0.82)",
        }}>
          <div style={{
            fontSize: "clamp(40px, 11vw, 82px)", fontWeight: 900, letterSpacing: 6,
            color: "#ff0044", marginBottom: 8,
            textShadow: "0 0 20px #ff0044, 0 0 60px #ff0044, 0 0 120px rgba(255,0,68,0.5)",
            animation: "fl 0.5s ease-in-out infinite alternate",
          }}>GAME OVER</div>
          <div style={{ color: "#ff00aa", fontSize: 12, letterSpacing: 5, marginBottom: 40,
            textShadow: "0 0 10px #ff00aa" }}>— YOU HAVE BEEN DELETED —</div>
          <div style={{
            display: "flex", gap: 56, marginBottom: 44,
            border: "1px solid rgba(255,0,170,0.25)", padding: "20px 52px",
            borderRadius: 4, background: "rgba(255,0,170,0.04)",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#554466", fontSize: 10, letterSpacing: 4, marginBottom: 4 }}>SCORE</div>
              <div style={{ color: "#00ffcc", fontSize: 36, fontWeight: 900,
                textShadow: "0 0 20px #00ffcc" }}>{score}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#554466", fontSize: 10, letterSpacing: 4, marginBottom: 4 }}>BEST</div>
              <div style={{ color: "#ff00aa", fontSize: 36, fontWeight: 900,
                textShadow: "0 0 20px #ff00aa" }}>{hiScore}</div>
            </div>
          </div>
          <button onClick={startGame} style={{
            background: "linear-gradient(135deg, #ff0044, #ff00aa)", border: "none",
            color: "#fff", fontSize: 15, letterSpacing: 6, padding: "15px 56px",
            borderRadius: 4, cursor: "pointer", fontFamily: "'Courier New', monospace",
            fontWeight: 900, textTransform: "uppercase",
            boxShadow: "0 0 40px rgba(255,0,68,0.8)",
          }}>▶ TRY AGAIN</button>
        </div>
      )}

      {/* D-PAD */}
      {phase === "playing" && (
        <div style={{
          position: "absolute", bottom: 24, right: 24, zIndex: 15,
          display: "grid",
          gridTemplateColumns: "56px 56px 56px",
          gridTemplateRows: "56px 56px 56px",
          gap: 6,
        }}>
          <span />{dpad(0, -1, "▲")}<span />
          {dpad(-1, 0, "◀")}
          <div style={{ width: 56, height: 56, borderRadius: 10,
            border: "1px solid rgba(255,0,170,0.15)", background: "rgba(255,0,170,0.03)" }} />
          {dpad(1, 0, "▶")}
          <span />{dpad(0, 1, "▼")}<span />
        </div>
      )}

      <style>{`
        @keyframes vp {
          0%,100% { filter: drop-shadow(0 0 24px #00ffcc) drop-shadow(0 0 48px #ff00aa); }
          50%      { filter: drop-shadow(0 0 48px #00ffcc) drop-shadow(0 0 96px #ff00aa); }
        }
        @keyframes bg {
          from { box-shadow: 0 0 24px rgba(255,0,170,0.6), 0 0 48px rgba(204,0,255,0.3); }
          to   { box-shadow: 0 0 50px rgba(255,0,170,1),   0 0 100px rgba(204,0,255,0.6); }
        }
        @keyframes fl {
          0%  {opacity:1} 88% {opacity:1} 90% {opacity:0.2} 92% {opacity:1} 96% {opacity:0.5} 100% {opacity:1}
        }
        * { box-sizing:border-box; margin:0; padding:0; }
        body { overflow:hidden; background:#0a0015; }
        button:focus { outline:none; }
      `}</style>
    </div>
  );
}
